import React, { useState, useEffect } from "react";
import {
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useSelector, useDispatch } from "react-redux";
import BranchEditModal from "../TabComponents/BranchEditModal"; // This component can be created similarly as CourseEditModal
import {
  fetchBranches,
  addBranch,
  updateBranch,
  deleteBranch,
} from "../../../../../utils/Apis"; // Adjust API paths

const BranchesTab = () => {
  const dispatch = useDispatch();
  const branches = useSelector((state) => state.data.branches?.branches || []);
  const loading = useSelector((state) => state.data.loading);
  const error = useSelector((state) => state.data.error);

  const [openModal, setOpenModal] = useState(false);
  const [editMode, setEditMode] = useState("add");
  const [selectedBranchId, setSelectedBranchId] = useState();
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    branchId: null,
    branchName: "",
  });

  // ✅ Fetch branches when component mounts
  useEffect(() => {
    if (!branches.length > 0) {
      dispatch(fetchBranches());
    }
  }, []);

  const handleAddClick = () => {
    setEditMode("add");
    setSelectedBranchId(null);
    setOpenModal(true);
  };

  const handleEditClick = (branchId) => {
    setEditMode("edit");
    setSelectedBranchId(branchId);
    setOpenModal(true);
  };

  const handleDeleteClick = (branchId, branchName) => {
    setDeleteDialog({
      open: true,
      branchId,
      branchName,
    });
  };

  const handleConfirmDelete = () => {
    dispatch(deleteBranch(deleteDialog.branchId))
      .unwrap()
      .then(() => {
        setNotification({
          open: true,
          message: "Branch deleted successfully",
          severity: "success",
        });
      })
      .catch((errorMsg) => {
        setNotification({
          open: true,
          message: `Failed to delete branch: ${errorMsg}`,
          severity: "error",
        });
      });

    // Close the dialog
    setDeleteDialog({ open: false, branchId: null, branchName: "" });
  };

  const handleCancelDelete = () => {
    setDeleteDialog({ open: false, branchId: null, branchName: "" });
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedBranchId(null);
  };

  const handleSubmitBranch = (submissionData) => {
    const { id, ...restOfData } = submissionData;
    if (editMode === "edit") {
      dispatch(updateBranch({ id, ...restOfData }))
        .unwrap()
        .then(() => {
          setNotification({
            open: true,
            message: "Branch updated successfully",
            severity: "success",
          });
        })
        .catch((errorMsg) => {
          setNotification({
            open: true,
            message: `Failed to update branch: ${errorMsg}`,
            severity: "error",
          });
        });
    } else {
      dispatch(addBranch(submissionData))
        .unwrap()
        .then(() => {
          setNotification({
            open: true,
            message: "Branch added successfully",
            severity: "success",
          });
        })
        .catch((errorMsg) => {
          setNotification({
            open: true,
            message: `Failed to add branch: ${errorMsg}`,
            severity: "error",
          });
        });
    }
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const handleGetDirections = (lat, lon) => {
    const googleMapsUrl = `https://www.google.com/maps/place/${lat},${lon}`;
    window.open(googleMapsUrl, "_blank");
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5" fontWeight="bold">
          Branches
        </Typography>
        <Button variant="contained" color="primary" onClick={handleAddClick}>
          Add New Branch
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {branches.length === 0 ? (
            <Grid item xs={12}>
              <Typography align="center" p={4}>
                No branches available. Click "Add New Branch" to create one.
              </Typography>
            </Grid>
          ) : (
            branches.map((branch) => (
              <Grid item xs={12} sm={6} md={4} key={branch._id}>
                <Card
                  sx={{
                    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 16px 30px rgba(0, 0, 0, 0.15)",
                    },
                    borderRadius: "16px",
                    overflow: "hidden",
                  }}
                >
                  <CardContent sx={{ textAlign: "center", p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                      {branch.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#7F8C8D",
                        mb: 2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {branch.location?.address}, {branch.location?.city},{" "}
                      {branch.location?.state}
                    </Typography>

                    {/* Display Phone and Email */}
                    <Typography
                      variant="body2"
                      sx={{ color: "#2c3e50", mb: 1 }}
                    >
                      Phone: {branch.contact?.phone || "Not available"}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "#2c3e50", mb: 2 }}
                    >
                      Email: {branch.contact?.email || "Not available"}
                    </Typography>

                    <Box
                      sx={{
                        justifyContent: "space-evenly",
                        display: "flex",
                        flexDirection: "row",
                        gap: 2,
                      }}
                    >
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => handleEditClick(branch._id)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() =>
                          handleDeleteClick(branch._id, branch.name)
                        }
                      >
                        Delete
                      </Button>
                      {/* Direction Button */}
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() =>
                          handleGetDirections(
                            branch.location?.coordinates[1], // lat
                            branch.location?.coordinates[0] // lon
                          )
                        }
                        sx={{
                          padding: "10px", // Adjust the size of the button
                          borderRadius: "50%", // Make the button rounded
                          minWidth: "40px", // Minimum width of the button
                          minHeight: "40px", // Minimum height of the button
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <ArrowForwardIcon />
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      )}

      {/* Branch Edit Modal */}
      <BranchEditModal
        open={openModal}
        onClose={handleCloseModal}
        onSubmit={handleSubmitBranch}
        mode={editMode}
        branchId={selectedBranchId}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={handleCancelDelete}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the branch "
            {deleteDialog.branchName}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BranchesTab;
